/**
 * Full set of validators for our objects (or any object, for that matter).
 * The idea here is to vastly reduce the size of the uploaded code.
 *
 * @module
 */

import {ValidationError} from './errors';
import {EntitySchema, SchemaField, uuid} from "../@types/schema";
import {EntityBase} from "../@types/entity";
import upFirstLetter from "./picos/upFirstLetter";

/**
 * Is this an array type?
 * Note: this does not test if a value is an array!
 *
 * @param {string} t - type
 * @returns {boolean}
 */
function isArrayType(t: string): boolean {
    return t.includes('[]')
}

/**
 * Converts our types into their base javascript types. This function is called from
 * getJsType to handle arrays.
 *
 * @param {string} type - the full type
 * @returns {string} - the base type
 */
function getBaseType(type: string): string {
    switch (type) {
        case 'uuid':
        case 'string':
        case 'char':
            return 'string';
        case 'hrtimestamp':
        case 'int':
        case 'bigint':
        case 'float':
        case 'money':
            return 'number';
        case 'date':
        case 'timestamp':
            return 'object';
        case 'boolean':
            return 'boolean';
        case 'json':
        case 'jsonb':
            return 'object';
        case 'array':
            return 'array';
        case 'binary':
            return 'binary';
        default:
            return type;
    }
}

/**
 * Returns the underlying type of the type, e.g. uuid[] => uuid, uuid => uuid
 *
 * @param {string} t - type
 * @returns {string} - the base type
 */
function underlyingType(t: string): string {
    if (isArrayType(t)) return t.slice(0, -2);
    else return t;
}

/**
 * Convert the schema type to a javascript type, e.g. uuid => string, uuid[] => array
 *
 * @param {string} t - e.g. uuid[] or string
 * @returns {string} The javascript type
 */
function getJsType(t: string): string {
    return getBaseType(underlyingType(t));
}

/**
 * Validate an object, one field at a time.
 *
 * @param {EntitySchema} s - schema
 * @param {object} o - object
 * @returns {boolean}
 */
function validateObject(s: EntitySchema, o: EntityBase): boolean {
    const fields = Object.keys(s.fields);
    for (const f of fields) {
        if (s.fields[f].required) validateField(f, s, o);
        else { // @ts-ignore
            if (!s.fields[f].required && !!o[f]) validateField(f, s, o);
        }
    }
    // Whole-entity rules, as the server's validateObject: refuse with the schema's message.
    for (const invariant of s.invariants ?? []) {
        if (!invariant.fn(o)) throw new ValidationError(invariant.message);
    }
    return true;
}

/**
 * Validate field f of object o using schema s. Returns true or a throws a ValidationError.
 *
 * Not that this function is meant to reduce code size, NOT be performant.
 *
 * @param {string} f - field
 * @param {EntitySchema} s - schema
 * @param {object} o - object to validate
 * @param {string} [t] - underlying object type
 * @throws {ValidationError}
 */
function validateField(f: string, s: EntitySchema, o: object, t?: string) {
    const {required, minLength, maxLength, minValue, maxValue, values} = s.fields[f] as SchemaField; // field descriptor

    // @ts-ignore
    const v: any = o[f];
    const type = t ?? s.fields[f].type;

    // Recurse over each value if array
    if (isArrayType(type)) {
        // If this is an array type and a required field and the value v is null, throw error
        if (required) validateRequired(f, v, type);

        // If not required and null, nothing more to check
        else if (!v) return true;

        // It exists, so check that it is actually an array
        validateArray(f, v);

        // Check each of its elements to ensure that it meets the field's requirements as well
        // v?.forEach(e => {
        //     validateField(f, s, e, underlyingType(type));
        // });
    }
    else {
        if (required) {
            validateRequired(f, v, underlyingType(type));
            validateType(f, v, underlyingType(type));
        }
        if (minLength) validateMinLength(f, v, minLength);
        if (maxLength) validateMaxLength(f, v, maxLength);
        if (minValue) validateMinValue(f, v, minValue);
        if (maxValue) validateMaxValue(f, v, maxValue);
        if (values) validateValues(f, v, values);
        return true;
    }
}

/**
 * Check if v has type t.
 *
 * @param {string} f - field
 * @param {any} v - value
 * @param {string} t - type (underlying only!)
 * @returns {boolean}
 */
function validateType(f: string, v: any, t: string): boolean {
    // UUID has special logic
    if (t === 'uuid') return validateUUID(f, v);
    else if (typeof (v) === getJsType(t)) return true;
    else if (getJsType(t) === 'number' && Number.parseFloat(v)) {
        console.warn(`Warning: ${f} is not a number: `, v, `is a ${typeof (v)}`);
        return true;
    }
    else throw new ValidationError(`${f} is not a ${t}: ${v}`);
}

/**
 * Check if v is an array of t.
 *
 * @param {string} f - field
 * @param {any} v - value
 * @returns {boolean}
 */
function validateArray(f: string, v: any): boolean {
    // If value is null, no more checks we can do
    if (!v) return true;

    if (!Array.isArray(v)) throw new ValidationError(`${f} is not an array: ${JSON.stringify(v)}`);
    else return true;
}

/**
 *
 * @param {string} f - field
 * @param {uuid} v - value
 * @returns {boolean}
 * @throws {ValidationError}
 */
function validateUUID(f: string, v: uuid): boolean {
    // If value is null, there are no more checks we can do
    if (!v) return true;

    // Check for UUID format
    else if (/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/.test(v)) return true;
    else throw new ValidationError(`${f} is not in UUID format: ${v}`);
}

/**
 * Validate min value of a number.
 *
 * @param {string} f - field
 * @param {number} v - value
 * @param {number} m - min value
 * @returns {boolean}
 * @throws {ValidationError}
 */
function validateMinValue(f: string, v: number, m: number): boolean {
    if (v < m) throw new ValidationError(`${f} is too small, minimum value is ${m}: ${v}`);
    else return true;
}

/**
 * Validate max value of a number.
 *
 * @param {string} f - field
 * @param {number} v - value
 * @param {number} m - max value
 * @returns {boolean}
 * @throws {ValidationError}
 */
function validateMaxValue(f: string, v: number, m: number): boolean {
    if (v > m) throw new ValidationError(`${f} is too large, maximum value is ${m}: ${v}`);
    else return true;
}

/**
 * Validate minimum length of a string.
 *
 * @param {string} f - field
 * @param {string} v - value
 * @param {number} l - length
 * @returns {boolean}
 * @throws {ValidationError}
 */
function validateMinLength(f: string, v: string, l: number): boolean {
    if (v?.length < l) throw new ValidationError(`${f} is too small, min ${l} characters: ${v}`);
    else return true;
}

/**
 * Validate maximum length of a string.
 *
 * @param {string} f - field
 * @param {string} v - value
 * @param {number} l - length
 * @returns {boolean}
 * @throws {ValidationError}
 */
function validateMaxLength(f: string, v: string, l: number): boolean {
    if (v?.length > l) throw new ValidationError(`${f} is too long, max ${l} characters: ${v}`);
    else return true;
}

/**
 * Validate if a value v is not null and of type t.
 *
 * @param {string} f - field
 * @param {any} v - value
 * @param {string} t - type
 * @throws {ValidationError}
 */
function validateRequired(f: string, v: any, t: string) {
    // Handle arrays
    if (isArrayType(t)) {
        if (Array.isArray(v) && v.length > 0) return true;
        else throw new ValidationError(`${upFirstLetter(f)} is required`);
    }

    // Handle UUIDs
    if (t === 'uuid') {
        if (v == null) throw new ValidationError(`${upFirstLetter(f)} is required`);
        else return validateUUID(f, v);
    }

    const jsType = getJsType(t);

    // Otherwise validate the jsType
    if ((jsType === 'number') && (v != null) && (v !== '') && !Number.isNaN(Number.parseFloat(v))) return true;
    else if (jsType === 'string') {
        if (v == null || v === '') throw new ValidationError(`${upFirstLetter(f)} is required`);
    }
    else if (typeof (v) === jsType) return true;
    else throw new ValidationError(`${upFirstLetter(f)} is required`);
}

/**
 * Test if v is one of the listed values in l.
 *
 * @param {string} f
 * @param {string | number} v
 * @param {string[] | number[]} l
 * @throws {ValidationError}
 */
function validateValues(f: string, v: string | number, l: string[] | number[]) {
    // If null, no further tests
    if (!v) return true;
    else { // @ts-ignore
        if (l.includes(v)) return true;
        else throw new ValidationError(`${f} is not one of [${l.join(',')}]: ${v}`);
    }
}

/**
 * Check if v is an object.
 *
 * @param {string} f - field
 * @param {any} v - value
 * @returns {boolean}
 * @throws {ValidationError}
 */
// function validateJson(f: string, v: any): boolean {
//     if (!v) return true;
//     else if (typeof (v) === 'object') return true;
//     else throw new ValidationError(`${f} is not valid JSON: ${JSON.stringify(v, null, 2)}`);
// }

export {validateObject, validateField};